'use strict';

function PerformanceReviewService($http, $interpolate, upload) {
  var round = {
    base: '/api/assessment/round/',
    detail: $interpolate('/api/assessment/round/{{id}}/'),
    detailRoute: $interpolate('/api/assessment/round/{{id}}/{{detail}}/')
  };

  var invite = {
    base: '/api/assessment/invite/',
    detail: $interpolate('/api/assessment/invite/{{id}}/'),
    updateTranspire: $interpolate('/api/assessment/invite/{{id}}/update_transpire/'),
    detailRoute: $interpolate('/api/assessment/invite/{{id}}/{{detail}}/'),
    'import': '/api/core/csv-parser/',
    asCoworker: 'api/assessment/invites-as-coworker'
  };

  var questionnaire = {
    detail: $interpolate('/api/assessment/questionnaire/{{token}}/'),
    detailRoute: $interpolate('/api/assessment/questionnaire/{{token}}/{{detail}}/')
  };

  var agreementQuestionnaire = {
    detail: $interpolate('/api/assessment/questionnaire-agreement/{{token}}/'),
    detailRoute: $interpolate('/api/assessment/questionnaire-agreement/{{token}}/{{detail}}/')
  };

  var distribution = {
    base: '/api/assessment/round-distribution/',
    detail: $interpolate('/api/assessment/round-distribution/{{id}}/'),
    detailRoute: $interpolate('/api/assessment/round-distribution/{{id}}/{{detail}}/')
  };

  var answer = $interpolate('/api/assessment/answer/{{id}}/');

  return {
    list: {
      round: listRound,
      roundsByInvitedUser: listRoundsByInvitedUser,
      invite: listInvite,
      recentRounds: listRecentRounds,
      invitesAsCoworker: listInvitesAsCoworker
    },

    retrieve: {
      questionnaire: retrieveQuestionnaire,
      summary: retrieveSummary,
      basis: retrieveBasis,
      distribution: retrieveDistribution,
      automaticDistribution: retrieveAutomaticDistribution,
      intro: retrieveIntro,
      agreement: {
        questionnaire: retrieveAgreementQuestionnaire,
        summary: retrieveAgreementSummary,
        agreementTurn: retrieveAgreementTurn
      }
    },

    create: {
      round: createRound,
      invite: createInvite,
      invitations: createInvitations,
      inviteTokens: createInviteTokens
    },

    update: {
      round: updateRound,
      invite: updateInvite,
      answer: updateAnswer,
      summary: updateSummary,
      distribution: updateDistribution,
      openForReview: openForReview,

      agreement: {
        answer: updateAgreementAnswer,
        summary: updateAgreementSummary
      }
    },

    'delete': {
      invite: deleteInvite,
      round: deleteRound
    },

    util: {
      inviteResend: inviteResend,
      'import': importInvites,
      updateTranspire: updateTranspire
    },

    urls: {
      invite: {
        'base':   $interpolate('/performance-review/{{round}}/invite'),
        'import': $interpolate('/performance-review/{{round}}/invite/import')
      },

      basis: {
        base: $interpolate('/performance-review/{{round}}/basis')
      },

      distribution: {
        base: $interpolate('/performance-review/{{round}}/distribution')
      },

      questionnaire: {
        base: $interpolate('/performance-review/questionnaire/{{token}}'),
        step: $interpolate('/performance-review/questionnaire/{{token}}/{{step}}')
      },

      agreement: {
        questionnaire: {
          base: $interpolate('/performance-review/agreement/questionnaire/{{token}}'),
          step: $interpolate('/performance-review/agreement/questionnaire/{{token}}/{{step}}')
        }
      }
    }
  };

  function listRound() {
    return $http.get(round.base);
  }

  function listRoundsByInvitedUser(employeeId) {
    return $http.get(round.base + '/listByInvitedUser', { params: {'employee': employeeId} });
  }

  function updateTranspire(transpire, id) {
    var update = {
      transpire: new Date(transpire).toISOString()
    };
    return $http({ url: invite.updateTranspire({ id: id }), method: 'POST', data: update });
  }

  function createRound(questionnaire, description, goals) {
    var data = { questionnaire: questionnaire, description: description, goals: goals };
    return $http.post(round.base, data);
  }

  function listInvite(id) {
    var params = { round: id };
    return $http.get(invite.base, { params: params });
  }

  function listRecentRounds() {
    return $http.get(round.base + '/employees_and_most_recent_rounds');
  }

  function listInvitesAsCoworker() {
    return $http.get(invite.asCoworker);
  }

  function retrieveQuestionnaire(token, step) {
    var params = { step: step };
    return $http.get(questionnaire.detail({token: token}), { params: params });
  }

  function retrieveAgreementQuestionnaire(token, step) {
    var params = { step: step };
    return $http.get(agreementQuestionnaire.detail({token: token}), { params: params });
  }

  function retrieveSummary(token) {
    return $http.get(questionnaire.detailRoute({token:token, detail: 'summary'}));
  }

  function retrieveAgreementSummary(token) {
    return $http.get(agreementQuestionnaire.detailRoute({token:token, detail: 'summary'}));
  }

  function retrieveAgreementTurn(token) {
    return $http.get(agreementQuestionnaire.detailRoute({token:token, detail: 'turn'}));
  }

  function retrieveBasis(id) {
    return $http.get(round.detailRoute({id: id, detail: 'basis'}));
  }

  function retrieveDistribution(id) {
    return $http.get(distribution.detail({id: id}));
  }

  function retrieveAutomaticDistribution(id) {
    return $http.get(distribution.detailRoute({id: id, detail: 'automatic_distribution'}));
  }

  function retrieveIntro(token) {
    return $http.get(questionnaire.detailRoute({token:token, detail: 'intro'}));
  }

  function createInvite(id, first_name, last_name, email, transpire) {
    return $http.post(invite.base, {
      round: id,
      first_name: first_name,
      last_name: last_name,
      email: email,
      transpire: new Date(transpire).toISOString()
    });
  }

  function createInvitations(id, transpire, users) {
    var invites = (users || []).map(function(user) {
      user.transpire = new Date(transpire).toISOString();
      return user;
    });

    return $http.post(invite.detailRoute({ id: id, detail: 'import' }), invites);
  }

  function createInviteTokens(id, subject, body, signature) {
    return $http.post(invite.detailRoute({ id: id, detail: 'invite' }), {
      subject: subject, message: body, signature: signature
    });
  }

  function updateRound(id, questionnaire, description, goals) {
    var update = { questionnaire: questionnaire, description: description, goals: goals };
    return $http({ url: round.detail({ id: id }), method: 'PATCH', data: update });
  }

  function openForReview(id, data) {
    return $http.post(round.detailRoute({ id: id, detail: 'open_review' }), data);
  }

  function updateInvite(id, first_name, last_name, transpire, subject, body, signature, email) {
    var update = {
      first_name: first_name,
      last_name: last_name,
      transpire: new Date(transpire).toISOString(),
      subject: subject,
      message: body,
      signature: signature
    };
    if (email) {
      update['email'] = email;
    }
    return $http({ url: invite.detail({ id: id }), method: 'PATCH', data: update });
  }

  function updateAnswer(id, choice, reflection, what, how, when, reviewComment) {
    var update = { choice: choice, reflection: reflection, what: what, how: how, when: when, review_comments: reviewComment };
    return $http({ url: answer({id: id}), method: 'PATCH', data: update});
  }

  function updateAgreementAnswer(id, choice, reflection, what, how, when) {
    var update = { choice: choice, reflection: reflection, what: what, how: how, when: when };
    return $http({ url: answer({id: id}), method: 'PATCH', data: update, params: { agreed_by_both: true }});
  }

  function deleteInvite(id) {
    return $http.delete(invite.detail({ id: id }));
  }

  function deleteRound(id) {
    return $http.delete(round.detail({ id: id }));
  }

  function inviteResend(id, subject, body, signature) {
    return $http.post(invite.detailRoute({ id: id, detail: 'resend_invite' }), {
      subject: subject, message: body, signature: signature
    });
  }

  function importInvites(file) {
    return upload({ url: invite.import, method: 'POST', data: { file: file } });
  }

  function updateSummary(token) {
    return $http({ method: 'PATCH', url: questionnaire.detail({token:token}), data: { completed: true } });
  }

  function updateDistribution(id, managerSum, employeeSums) {
    var data = { 'manager_sum': managerSum, 'employee_sums': employeeSums };
    return $http({ url: distribution.detail({id: id}), method: 'PATCH', data: data});
  }

  function updateAgreementSummary(token, approved) {
    return $http({ method: 'PATCH', url: agreementQuestionnaire.detail({token:token}), data: { approved: approved || false } });
  }
}

module.exports = PerformanceReviewService;



/*****************
 ** WEBPACK FOOTER
 ** ./src/app/performance-review/service/performance-review/index.js
 ** module id = 258
 ** module chunks = 0
 **/